import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deleteMunicipalitiesByMunicipalityIdRolesByRoleIdMutation, getMunicipalitiesByMunicipalityIdPermissionsOptions, getMunicipalitiesByMunicipalityIdRolesOptions, getMunicipalitiesByMunicipalityIdRolesQueryKey, getRolesOptions, patchMunicipalitiesByMunicipalityIdRolesByRoleIdMutation, postMunicipalitiesByMunicipalityIdRolesMutation, } from '@citylytics/core-api-ts-client/react-query';
import { LIST_PAGE_MAX_SIZE } from '@citylytics/http/constants';
import { readWholeList } from '../core-api-client/core-api-client.utils.js';
import { selectWholeMunicipalityRoles } from './roles.utils.js';
/**
 * A municipality's roles with their permissions, read whole. `enabled` is the caller's: only
 * someone who may read them should ask, or the request only 403s.
 */
export function useMunicipalityRolesQuery(params) {
    const { municipalityId, enabled } = params;
    return useQuery({
        ...getMunicipalitiesByMunicipalityIdRolesOptions({
            path: { municipalityId },
            query: { size: LIST_PAGE_MAX_SIZE },
        }),
        select: selectWholeMunicipalityRoles,
        enabled,
    });
}
/** Every global role with its permissions, read whole; for staff, so the caller gates it. */
export function useGlobalRolesQuery(params) {
    return useQuery({
        ...getRolesOptions({ query: { size: LIST_PAGE_MAX_SIZE } }),
        select: (response) => readWholeList({
            items: response.data.roles,
            hasMore: response.data.pagination.hasMore,
            listName: 'global roles',
        }),
        enabled: params.enabled,
    });
}
/**
 * What a municipality's roles may hold (its ceiling), read whole; the choices a municipality
 * role's permission grid offers.
 */
export function useMunicipalityPermissionsQuery(params) {
    const { municipalityId, enabled } = params;
    return useQuery({
        ...getMunicipalitiesByMunicipalityIdPermissionsOptions({
            path: { municipalityId },
            query: { size: LIST_PAGE_MAX_SIZE },
        }),
        select: (response) => readWholeList({
            items: response.data.permissions,
            hasMore: response.data.pagination.hasMore,
            listName: 'municipality permissions',
        }),
        enabled,
    });
}
/**
 * Create, update and delete for a municipality's roles; each refreshes its role list. A failure
 * stays on the mutation, so it shows where it was asked for: the create form, the save bar, the
 * delete confirmation.
 */
export function useMunicipalityRoleMutations(params) {
    const { municipalityId, onChanged, onSaved } = params;
    const queryClient = useQueryClient();
    const queryKey = getMunicipalitiesByMunicipalityIdRolesQueryKey({ path: { municipalityId } });
    const refresh = () => Promise.all([queryClient.invalidateQueries({ queryKey }), onChanged()]);
    return {
        create: useMutation({
            ...postMunicipalitiesByMunicipalityIdRolesMutation(),
            onSuccess: refresh,
        }),
        update: useMutation({
            ...patchMunicipalitiesByMunicipalityIdRolesByRoleIdMutation(),
            onSuccess: async () => {
                await refresh();
                onSaved();
            },
        }),
        remove: useMutation({
            ...deleteMunicipalitiesByMunicipalityIdRolesByRoleIdMutation(),
            onSuccess: refresh,
        }),
    };
}
